import { useState, useEffect } from 'react';
import './App.css';
import MainLayout from './components/Editor/MainLayout';
import DashboardView from './components/Dashboard/DashboardView';
import BottomTabBar from './components/Navigation/BottomTabBar';
import PlantListView from './components/PlantList/PlantListView';
import SettingsView from './components/Settings/SettingsView';
import AppCrash from './components/ErrorStates/AppCrash';
import SessionExpiredModal from './components/ErrorStates/SessionExpiredModal';
import AgbUpdateModal from './components/Auth/AgbUpdateModal';
import ClaimGardensPrompt from './components/Auth/ClaimGardensPrompt';
import SplashScreen from './components/Onboarding/SplashScreen';
import OnboardingFlow from './components/Onboarding/OnboardingFlow';
import InstallPromptSheet from './components/Dashboard/InstallPromptSheet';
import GardenStartupLoader from './components/GardenStartupLoader';
import { GardenSettingsProvider, useGardenSettings } from './context/GardenSettingsContext';
import { WizardProvider } from './context/WizardContext';
import { PlantProvider } from './context/PlantContext';
import { DashboardProvider, useDashboard } from './context/DashboardContext';
import { AuthProvider } from './context/AuthContext';
import useAuth from './hooks/useAuth';
import { useBreakpoint } from './hooks/useBreakpoint';
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import { canInstall, subscribeToInstallAvailability } from './lib/installPrompt';

const ONBOARDING_KEY = 'mgr_onboarding_complete';
const VISIT_COUNT_KEY = 'mgr_visit_count';
const INSTALL_DISMISSED_KEY = 'mgr_install_dismissed';
// Cooldown before the install prompt may re-appear after a "Später" dismiss
// (#488). Kept defensively long so re-offering stays calm, not pushy.
const INSTALL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Determine which top-level page to render from the current URL.
 *
 * /login?token=...  → VerifyPage  (magic link click)
 * /login            → LoginPage   (user navigates directly)
 * anything else     → main app
 */
function getPage() {
  const path = window.location.pathname;
  const search = window.location.search;
  if (path === '/login') {
    return search.includes('token=') ? 'verify' : 'login';
  }
  return 'app';
}

/**
 * AppContent — renders Dashboard or Editor based on activeView.
 * Mobile: bottom-tab navigation; Desktop: existing view switching.
 * Must be inside DashboardProvider to access context.
 */
function AppContent() {
  const { activeView, mobileTab, settingsOpen, setSettingsOpen } = useDashboard();
  const { isMobile } = useBreakpoint();

  if (!isMobile) {
    return (
      <>
        {activeView === 'dashboard' ? <DashboardView /> : <MainLayout />}
        {/* Desktop settings/account panel — opened from the header gear (#529).
            Mobile reaches the same SettingsView via the "mehr" bottom tab. */}
        {settingsOpen && (
          <div className="fixed inset-0 z-50 bg-gray-50" role="dialog" aria-modal="true">
            <SettingsView onClose={() => setSettingsOpen(false)} />
          </div>
        )}
      </>
    );
  }

  const mobileViews = {
    uebersicht:     <DashboardView />,
    pflegekalender: <DashboardView />,
    garten:         <PlantListView />,
    mehr:           <SettingsView />,
    editor:         <MainLayout />,
  };

  return (
    <>
      <div className="pb-[68px]" style={{ minHeight: '100dvh' }}>
        {mobileViews[mobileTab] ?? <DashboardView />}
      </div>
      <BottomTabBar />
    </>
  );
}

/**
 * Shows the SessionExpiredModal when a 401 fires after an active session.
 * Must be inside AuthProvider.
 */
function SessionExpiredGuard({ children }) {
  const { sessionExpired } = useAuth();
  return (
    <>
      {children}
      {sessionExpired && <SessionExpiredModal />}
    </>
  );
}

/**
 * Blockt das Mounten der App, solange der User die aktualisierten AGB nicht
 * re-akzeptiert hat (#245, Chunk E). Muss innerhalb von AuthProvider liegen.
 * Wenn re-akzeptiert oder kein User → children werden normal gerendert.
 */
function AgbReacceptanceGuard({ children }) {
  const { needsAgbReacceptance } = useAuth();
  if (needsAgbReacceptance) {
    return <AgbUpdateModal />;
  }
  return children;
}

/**
 * Whether the PWA install prompt is eligible to show, based on the stored visit
 * count, dismiss/cooldown state (#488), standalone display mode and whether the
 * browser can install at all (#680). Pure reads — no state — so it can run in a
 * render-phase decision. Exported for tests.
 */
export function shouldShowInstallPrompt() {
  // Dismiss handling (#488): a numeric value is a "Später" timestamp → suppress
  // only until the cooldown elapses; a non-numeric value ('true', i.e. accepted
  // install or a legacy dismiss) is a permanent suppress.
  const dismissed = localStorage.getItem(INSTALL_DISMISSED_KEY);
  if (dismissed) {
    const dismissedAt = Number(dismissed);
    if (!Number.isFinite(dismissedAt)) return false; // permanent ('true')
    if (Date.now() - dismissedAt < INSTALL_COOLDOWN_MS) return false; // within cooldown
  }
  // Already installed (Chromium standalone or iOS Safari standalone) — skip prompt
  const isStandalone =
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;
  if (isStandalone) return false;
  // Never offer what this browser cannot deliver (#680): without a captured
  // `beforeinstallprompt` and outside iOS there is no way to install, and the
  // primary button would do nothing but close the sheet.
  if (!canInstall()) return false;
  const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
  return count >= 3;
}

/**
 * Shows the PWA install prompt on the 3rd page load (stored visit count
 * becomes >= 3) once the wizard is complete — and only where the browser can
 * actually install the app (#680). Must be inside GardenSettingsProvider.
 */
function InstallPromptGuard({ children }) {
  const { isConfigured } = useGardenSettings();
  const [show, setShow] = useState(() => isConfigured && shouldShowInstallPrompt());
  const [prevIsConfigured, setPrevIsConfigured] = useState(isConfigured);

  // Reveal once the wizard completes (isConfigured flips true). Decided during
  // render rather than in an effect to avoid a setState-in-effect cascade.
  if (isConfigured !== prevIsConfigured) {
    setPrevIsConfigured(isConfigured);
    if (isConfigured && shouldShowInstallPrompt()) {
      setShow(true);
    }
  }

  // Chromium decides asynchronously whether the app is installable, so
  // `beforeinstallprompt` can arrive after this component mounted. Without
  // re-checking here, the gate above would have already said "no" and the offer
  // would be postponed to the next page load.
  useEffect(() => {
    return subscribeToInstallAvailability(() => {
      if (isConfigured && shouldShowInstallPrompt()) {
        setShow(true);
      }
    });
  }, [isConfigured]);

  return (
    <>
      {children}
      {show && <InstallPromptSheet onDismiss={() => setShow(false)} />}
    </>
  );
}

function App() {
  const page = getPage();

  // First-run flow: splash → onboarding → app (only for main app page)
  const [appPhase, setAppPhase] = useState(() => {
    if (page !== 'app') return 'app';
    if (localStorage.getItem(ONBOARDING_KEY)) return 'app';
    return 'splash';
  });

  // Increment visit count once per app load
  useEffect(() => {
    if (page !== 'app') return;
    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    localStorage.setItem(VISIT_COUNT_KEY, String(count + 1));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (page === 'verify') {
    return (
      <AppCrash>
        <AuthProvider>
          <VerifyPage />
        </AuthProvider>
      </AppCrash>
    );
  }

  if (page === 'login') {
    return (
      <AppCrash>
        <AuthProvider>
          <LoginPage />
        </AuthProvider>
      </AppCrash>
    );
  }

  // First-run screens (no providers needed)
  if (appPhase === 'splash') {
    return (
      <AppCrash>
        <SplashScreen onDone={() => setAppPhase('onboarding')} />
      </AppCrash>
    );
  }

  if (appPhase === 'onboarding') {
    return (
      <AppCrash>
        <OnboardingFlow
          onDone={() => {
            localStorage.setItem(ONBOARDING_KEY, 'true');
            setAppPhase('app');
          }}
        />
      </AppCrash>
    );
  }

  return (
    <AppCrash>
      <AuthProvider>
        <AgbReacceptanceGuard>
          <GardenSettingsProvider>
            <DashboardProvider>
              <WizardProvider>
                <PlantProvider>
                  <SessionExpiredGuard>
                    <InstallPromptGuard>
                      {/* Single backend-authoritative garden load on startup,
                          shared by Dashboard and Editor (#484). */}
                      <GardenStartupLoader />
                      <div style={{ width: '100vw', height: '100vh' }}>
                        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4 pointer-events-none">
                          <div className="pointer-events-auto">
                            <ClaimGardensPrompt />
                          </div>
                        </div>
                        <AppContent />
                      </div>
                    </InstallPromptGuard>
                  </SessionExpiredGuard>
                </PlantProvider>
              </WizardProvider>
            </DashboardProvider>
          </GardenSettingsProvider>
        </AgbReacceptanceGuard>
      </AuthProvider>
    </AppCrash>
  );
}

export default App;
